import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Modal, SafeAreaView, ActivityIndicator, Switch, Platform, Image } from 'react-native';
import AppTextInput from '../components/AppTextInput';
import { AuthManager, BusinessOwner } from '../utils/authManager';
import SimpleStorage from '../utils/simpleStorage';
import { Product, ProductOption, ProductOptionValue, HALAL_PRODUCT_CATEGORIES } from '../interfaces/Product';
import { PRODUCT_TEMPLATES, getTemplateByBusinessType, createProductsFromTemplate } from '../utils/productTemplates';
import productSubscriptionService from '../services/productSubscriptionService';
import productManagementService from '../services/productManagementService';
import templateService from '../services/templateService';
import {
  uploadProductImage,
  ACCEPTED_FORMATS_LABEL,
  MAX_FILE_SIZE_BYTES,
  RECOMMENDED_IMAGE_WIDTH,
  RECOMMENDED_IMAGE_HEIGHT,
  RECOMMENDED_FILE_SIZE_KB,
} from '../services/productImageUploadService';
import UpgradePrompt from '../components/UpgradePrompt';
import { getWidgetFeaturedUsage, MAX_WIDGET_FEATURED_SLOTS } from '../utils/widgetFeaturedLimits';

interface ProductManagementScreenProps {
  onBack: () => void;
  onNavigateToSubscription?: () => void;
}

/** RFC4180-ish CSV parser: handles quoted fields containing commas, quotes, and newlines. */
function parseCSV(content: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    if (inQuotes) {
      if (char === '"') {
        if (content[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\r') {
      // skip; \n (below) terminates the row
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter(r => !(r.length === 1 && r[0].trim() === ''));
}

function csvEscapeField(value: unknown): string {
  const str = value === null || value === undefined ? '' : String(value);
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

const PRODUCT_CSV_HEADER = [
  'Product ID', 'Product Name', 'Description', 'Price', 'Unit', 'Category', 'Subcategory',
  'Active', 'Inventory (In Stock)', 'Stock Quantity', 'Weight', 'Preparation Time',
  'Specifications', 'Tags', 'Product Images',
  'Option 1 Name', 'Option 1 Values', 'Option 1 Required',
  'Option 2 Name', 'Option 2 Values', 'Option 2 Required',
  'Option 3 Name', 'Option 3 Values', 'Option 3 Required',
];

const ProductManagementScreen: React.FC<ProductManagementScreenProps> = ({ onBack, onNavigateToSubscription }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showCSVImportModal, setShowCSVImportModal] = useState(false);
  const [csvFileName, setCsvFileName] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [businessOwner, setBusinessOwner] = useState<BusinessOwner | null>(null);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);
  const [editingValueIndex, setEditingValueIndex] = useState<{ optionIndex: number; valueIndex: number } | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
  const [imagesPerProduct, setImagesPerProduct] = useState(3);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const imageFileInputRef = useRef<HTMLInputElement | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    unit: 'lb',
    category: '',
    subcategory: '',
    weight: '',
    stockQuantity: '',
    specifications: '',
    preparationTime: '',
    icon: '🥩',
    tags: '',
    images: [] as string[],
    imageThumbnails: [] as string[],
  });

  const productIcons = [
    '🥩', '🍗', '🍖', '🥓', '🦃', '🐟', '🦐', '🦀', '🧀', '🥚',
    '🍞', '🥖', '🥨', '🧈', '🥛', '🍯', '🧂', '🌶️', '🧄', '🧅'
  ];

  const units = ['lb', 'kg', 'piece', 'dozen', 'package', 'custom'];

  useEffect(() => {
    loadProducts();
    loadSubscriptionStatus();
  }, []);

  const loadSubscriptionStatus = async () => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (businessOwner) {
        const status = await productSubscriptionService.getStatus(businessOwner.id);
        console.log('📊 Loaded subscription status:', status);
        // Update activeProducts count from local products
        const localActiveProducts = products.filter(p => p.isActive).length;
        const updatedStatus = {
          ...status,
          activeProducts: localActiveProducts, // Use local count for accuracy
          // Ensure productLimit is a valid number (default to 10 if invalid)
          productLimit: (status.productLimit && typeof status.productLimit === 'number' && status.productLimit > 0) 
            ? status.productLimit 
            : (status.productLimit === 'Unlimited' || status.productLimit === -1) 
              ? 'Unlimited' 
              : 10
        };
        console.log('📊 Setting subscription status:', updatedStatus);
        setSubscriptionStatus(updatedStatus);
        // Resolve imagesPerProduct from plan (plans we sell)
        try {
          const plans = await productSubscriptionService.getPlans();
          const planId = status.planId != null ? String(status.planId) : 'free';
          const plan = plans.find((p: { id: string }) => p.id === planId);
          const max = plan?.imagesPerProduct;
          const cap = typeof max === 'number' ? max : (max === 'Unlimited' ? 20 : 3);
          setImagesPerProduct(cap);
        } catch (_) {
          setImagesPerProduct(3);
        }
      }
    } catch (error) {
      console.error('Error loading subscription status:', error);
    }
  };

  // Helper function to check product limit using subscription status
  const checkProductLimit = (newActiveProductsCount: number): { canAdd: boolean; message: string } => {
    // Only count active products (inactive products don't count toward the limit)
    const currentActive = products.filter(p => p.isActive).length;
    const totalAfter = currentActive + newActiveProductsCount;

    // Default to free plan limit if no subscription status
    let limit = 10;
    let planName = 'Free';

    if (subscriptionStatus) {
      // Ensure limit is a valid number (not 0, null, or undefined)
      const statusLimit = subscriptionStatus.productLimit;
      if (statusLimit && typeof statusLimit === 'number' && statusLimit > 0) {
        limit = statusLimit;
      } else if (statusLimit === 'Unlimited' || statusLimit === -1) {
        return { canAdd: true, message: '' };
      }
      planName = subscriptionStatus.planName || 'Free';
    }

    console.log('🔍 Checking limit:', {
      currentActive,
      newActiveProductsCount,
      totalAfter,
      limit,
      planName,
      subscriptionStatusExists: !!subscriptionStatus
    });

    if (totalAfter > limit) {
      const remaining = Math.max(0, limit - currentActive);
      return {
        canAdd: false,
        message: `You can only add ${remaining} more active product${remaining !== 1 ? 's' : ''} with your ${planName} plan (${limit} product limit). You currently have ${currentActive} active product${currentActive !== 1 ? 's' : ''} and are trying to add ${newActiveProductsCount} active product${newActiveProductsCount !== 1 ? 's' : ''} (${totalAfter} total, which exceeds the limit).`
      };
    }

    return { canAdd: true, message: '' };
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      const owner = await AuthManager.getSavedBusinessOwner();
      if (owner) {
        setBusinessOwner(owner);
        
        // Use productManagementService to load products (consistent with other parts of the app)
        const loadedProducts = await productManagementService.loadProducts(owner.id);
        console.log(`📦 ProductManagementScreen: Loaded ${loadedProducts.length} products`);
        
        if (loadedProducts.length > 0) {
          setProducts(loadedProducts);
          
          // Update subscription status with actual product count
          const localActiveProducts = loadedProducts.filter((p: Product) => p.isActive).length;
          if (subscriptionStatus) {
            setSubscriptionStatus({
              ...subscriptionStatus,
              activeProducts: localActiveProducts,
            });
          }
        } else {
          // No products found - start with empty list
          setProducts([]);
        }
      }
    } catch (error) {
      console.error('Error loading products:', error);
      Alert.alert('Error', 'Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const saveProducts = async (productsToSave: Product[], businessOwnerId: string) => {
    try {
      // Use productManagementService to save products (consistent with other parts of the app)
      await productManagementService.saveProducts(businessOwnerId, productsToSave);
      console.log(`✅ ProductManagementScreen: Saved ${productsToSave.length} products`);
    } catch (error) {
      console.error('Error saving products:', error);
      throw error;
    }
  };

  const handleImageFileSelect = async (e: any) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (formData.images.length >= imagesPerProduct) {
      Alert.alert('Limit reached', `Your plan allows up to ${imagesPerProduct} images per product.`);
      return;
    }
    // Fresh lookup rather than trusting the businessOwner state variable,
    // which is only set once on initial mount and can go stale/null (e.g.
    // if that initial load hit a Firestore timeout) without anything else
    // on this screen visibly breaking, since other actions re-fetch it.
    const owner = businessOwner || (await AuthManager.getSavedBusinessOwner());
    if (!owner) {
      Alert.alert('Error', 'Business owner not found. Please try again.');
      return;
    }
    setUploadingImage(true);
    try {
      const { url, thumbnailUrl } = await uploadProductImage(owner.id, file, editingProduct?.id);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, url],
        imageThumbnails: [...prev.imageThumbnails, thumbnailUrl],
      }));
      if (Platform.OS === 'web' && imageFileInputRef.current) imageFileInputRef.current.value = '';
    } catch (err: any) {
      Alert.alert('Upload failed', err?.message || 'Could not upload image.');
    } finally {
      setUploadingImage(false);
    }
  };

  const addImageUrl = () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    if (formData.images.length >= imagesPerProduct) {
      Alert.alert('Limit reached', `Your plan allows up to ${imagesPerProduct} images per product.`);
      return;
    }
    // Externally-hosted URLs have no generated thumbnail; fall back to the full URL at render time.
    setFormData(prev => ({ ...prev, images: [...prev.images, url], imageThumbnails: [...prev.imageThumbnails, ''] }));
    setImageUrlInput('');
  };

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
      imageThumbnails: prev.imageThumbnails.filter((_, i) => i !== index),
    }));
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductOptions([]);
    setImageUrlInput('');
    setFormData({
      name: '',
      description: '',
      price: '',
      unit: 'lb',
      category: '',
      subcategory: '',
      weight: '',
      stockQuantity: '',
      specifications: '',
      preparationTime: '',
      icon: '🥩',
      tags: '',
      images: [],
      imageThumbnails: [],
    });
    setShowAddModal(true);
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProductOptions(product.options || []);
    setImageUrlInput('');
    const images = [...(product.images || [])];
    // Pad/truncate thumbnails to match images 1:1; legacy products saved before
    // thumbnails existed fall back to the full image at each index.
    const thumbnails = images.map((_, i) => product.imageThumbnails?.[i] || '');
    setFormData({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      unit: product.unit,
      category: product.category,
      subcategory: product.subcategory,
      weight: product.weight?.toString() || '',
      stockQuantity: product.stockQuantity?.toString() || '',
      specifications: formatSpecifications(product.specifications),
      preparationTime: product.preparationTime.toString(),
      icon: product.icon,
      tags: product.tags.join(', '),
      images,
      imageThumbnails: thumbnails,
    });
    setShowAddModal(true);
  };

  const handleSaveProduct = async () => {
    try {
      if (!formData.name || !formData.price || !formData.category) {
        Alert.alert('Error', 'Please fill in all required fields');
        return;
      }

      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) {
        Alert.alert('Error', 'Business owner not found');
        return;
      }

      // Check product limit before adding (only for new products)
      if (!editingProduct) {
        const limitCheck = checkProductLimit(1);
        if (!limitCheck.canAdd) {
          // Reload subscription status to get current plan info
          const status = await productSubscriptionService.getStatus(businessOwner.id);
          const localActiveProducts = products.filter(p => p.isActive).length;
          setSubscriptionStatus({
            ...status,
            activeProducts: localActiveProducts,
          });
          setShowUpgradePrompt(true);
          Alert.alert('Product Limit Reached', limitCheck.message);
          return;
        }
      }

      const newProduct: Product = {
        id: editingProduct?.id || Date.now().toString(),
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price),
        unit: formData.unit as any,
        category: formData.category,
        subcategory: formData.subcategory,
        isActive: editingProduct?.isActive ?? true,
        isInStock: editingProduct?.isInStock ?? true,
        stockQuantity: formData.stockQuantity ? parseInt(formData.stockQuantity) : undefined,
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        specifications: formData.specifications.split(',').map(spec => spec.trim()).filter(spec => spec),
        halalCertified: formData.category.toLowerCase().includes('halal'),
        preparationTime: parseInt(formData.preparationTime) || 0,
        icon: formData.icon,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        images: formData.images.length > 0 ? formData.images : undefined,
        imageThumbnails: formData.images.length > 0 ? formData.images.map((_, i) => formData.imageThumbnails[i] || formData.images[i]) : undefined,
        businessId: businessOwner.id,
        createdAt: editingProduct?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        options: productOptions.length > 0 ? productOptions : undefined
      };

      let updatedProducts;
      if (editingProduct) {
        updatedProducts = products.map(p => p.id === editingProduct.id ? newProduct : p);
      } else {
        updatedProducts = [...products, newProduct];
      }

      setProducts(updatedProducts);
      await saveProducts(updatedProducts, businessOwner.id);
      
      // Update subscription status with new product count
      if (!editingProduct) {
        // Increment product count in background
        productSubscriptionService.incrementCount(businessOwner.id).catch(() => {});
        
        // Update local subscription status immediately
        if (subscriptionStatus) {
          const newActiveProducts = updatedProducts.filter(p => p.isActive).length;
          setSubscriptionStatus({
            ...subscriptionStatus,
            activeProducts: newActiveProducts,
          });
        }
        await loadSubscriptionStatus();
      }
      
      setShowAddModal(false);
      Alert.alert('Success', `Product ${editingProduct ? 'updated' : 'added'} successfully!`);
    } catch (error) {
      console.error('Error saving product:', error);
      Alert.alert('Error', 'Failed to save product. Please try again.');
    }
  };

  // Product Options Management Functions
  const handleAddOption = () => {
    if (productOptions.length >= 3) {
      Alert.alert('Limit Reached', 'You can add up to 3 customizable options per product.');
      return;
    }
    const newOption: ProductOption = {
      id: Date.now().toString(),
      name: '',
      values: [],
      isRequired: false
    };
    setProductOptions([...productOptions, newOption]);
    setEditingOptionIndex(productOptions.length);
  };

  const handleRemoveOption = (index: number) => {
    const updatedOptions = productOptions.filter((_, i) => i !== index);
    setProductOptions(updatedOptions);
    setEditingOptionIndex(null);
  };

  const handleUpdateOptionName = (index: number, name: string) => {
    const updatedOptions = [...productOptions];
    updatedOptions[index].name = name;
    setProductOptions(updatedOptions);
  };

  const handleToggleOptionRequired = (index: number) => {
    const updatedOptions = [...productOptions];
    updatedOptions[index].isRequired = !updatedOptions[index].isRequired;
    setProductOptions(updatedOptions);
  };

  const handleAddOptionValue = (optionIndex: number) => {
    const updatedOptions = [...productOptions];
    const newValue: ProductOptionValue = {
      id: Date.now().toString(),
      name: '',
      priceModifier: undefined
    };
    updatedOptions[optionIndex].values.push(newValue);
    setProductOptions(updatedOptions);
    setEditingValueIndex({ optionIndex, valueIndex: updatedOptions[optionIndex].values.length - 1 });
  };

  const handleRemoveOptionValue = (optionIndex: number, valueIndex: number) => {
    const updatedOptions = [...productOptions];
    updatedOptions[optionIndex].values = updatedOptions[optionIndex].values.filter((_, i) => i !== valueIndex);
    setProductOptions(updatedOptions);
    setEditingValueIndex(null);
  };

  const handleUpdateOptionValue = (optionIndex: number, valueIndex: number, field: 'name' | 'priceModifier', value: string | number) => {
    const updatedOptions = [...productOptions];
    if (field === 'name') {
      updatedOptions[optionIndex].values[valueIndex].name = value as string;
    } else {
      updatedOptions[optionIndex].values[valueIndex].priceModifier = value as number;
    }
    setProductOptions(updatedOptions);
  };

  const handleToggleActive = async (product: Product) => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) return;

      const updatedProduct = { ...product, isActive: !product.isActive, updatedAt: new Date().toISOString() };
      const updatedProducts = products.map(p => p.id === product.id ? updatedProduct : p);
      
      setProducts(updatedProducts);
      await saveProducts(updatedProducts, businessOwner.id);
      
      // Show confirmation message
      const status = updatedProduct.isActive ? 'activated' : 'deactivated';
      Alert.alert('Success', `Product "${product.name}" has been ${status}.`);
    } catch (error) {
      console.error('Error toggling product status:', error);
      Alert.alert('Error', 'Failed to update product status.');
    }
  };

  const handleToggleStock = async (product: Product) => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) return;

      const updatedProduct = { 
        ...product, 
        isInStock: !product.isInStock, 
        updatedAt: new Date().toISOString() 
      };
      const updatedProducts = products.map(p => p.id === product.id ? updatedProduct : p);
      
      setProducts(updatedProducts);
      await saveProducts(updatedProducts, businessOwner.id);
      
      // Show confirmation message
      const status = updatedProduct.isInStock ? 'back in stock' : 'marked as out of stock';
      Alert.alert('Stock Updated', `Product "${product.name}" has been ${status}.`);
    } catch (error) {
      console.error('Error toggling stock status:', error);
      Alert.alert('Error', 'Failed to update stock status.');
    }
  };

  const handleUpdateStockQuantity = async (product: Product, newQuantity: number) => {
    try {
      const businessOwner = await AuthManager.getSavedBusinessOwner();
      if (!businessOwner) return;

      const updatedProduct = { 
        ...product, 
        stockQuantity: newQuantity,
        isInStock: newQuantity > 0,
        updatedAt: new Date().toISOString() 
      };
      const updatedProducts = products.map(p => p.id === product.id ? updatedProduct : p);
      
      setProducts(updatedProducts);
      await saveProducts(updatedProducts, businessOwner.id);
      
      // Check if low stock threshold is reached
      if (product.lowStockThreshold && newQuantity <= product.lowStockThreshold) {
        Alert.alert(
          'Low Stock Alert', 
          `"${product.name}" is running low! Only ${newQuantity} left in stock.`
        );
      }
    } catch (error) {
      console.error('Error updating stock quantity:', error);
      Alert.alert('Error', 'Failed to update stock quantity.');
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    if (!businessOwner) {
      if (Platform.OS === 'web') {
        window.alert('Error: Business owner not found. Please log in again.');
      } else {
        Alert.alert('Error', 'Business owner not found. Please log in again.');
      }
      return;
    }

    const confirmMessage = `Are you sure you want to delete "${product.name}"? This action cannot be undone.`;
    
    if (Platform.OS === 'web') {
      // Use window.confirm for web
      if (window.confirm(confirmMessage)) {
        try {
          // Remove product from list
          const updatedProducts = products.filter(p => p.id !== product.id);
          setProducts(updatedProducts);
          
          // Save updated products list
          await saveProducts(updatedProducts, businessOwner.id);
          
          // Update subscription status immediately
          if (product.isActive) {
            // Decrement product count in background
            productSubscriptionService.decrementCount(businessOwner.id).catch(() => {});
          }
          
          // Update local subscription status immediately
          const newActiveProducts = updatedProducts.filter(p => p.isActive).length;
          if (subscriptionStatus) {
            setSubscriptionStatus({
              ...subscriptionStatus,
              activeProducts: newActiveProducts,
            });
          }
          
          window.alert(`"${product.name}" has been deleted successfully!`);
        } catch (error) {
          console.error('Error deleting product:', error);
          window.alert('Failed to delete product. Please try again.');
        }
      }
    } else {
      // Use Alert.alert for mobile
    Alert.alert(
      'Delete Product',
        confirmMessage,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
                // Remove product from list
              const updatedProducts = products.filter(p => p.id !== product.id);
              setProducts(updatedProducts);
                
                // Save updated products list
              await saveProducts(updatedProducts, businessOwner.id);
                
                // Update subscription status immediately
                if (product.isActive) {
                  // Decrement product count in background
                  productSubscriptionService.decrementCount(businessOwner.id).catch(() => {});
                }
                
                // Update local subscription status immediately
                const newActiveProducts = updatedProducts.filter(p => p.isActive).length;
                if (subscriptionStatus) {
                  setSubscriptionStatus({
                    ...subscriptionStatus,
                    activeProducts: newActiveProducts,
                  });
                }
                
                Alert.alert('Success', `"${product.name}" has been deleted successfully!`);
            } catch (error) {
              console.error('Error deleting product:', error);
                Alert.alert('Error', 'Failed to delete product. Please try again.');
            }
          }
        }
      ]
    );
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!businessOwner || products.length === 0) {
      Alert.alert('Error', 'Please add products before saving as a template.');
      return;
    }

    // Prompt for template name
    if (Platform.OS === 'web') {
      const templateName = window.prompt('Enter a name for this template:', `${businessOwner.companyName || 'My'} Product Template`);
      if (!templateName) return;
      await saveTemplateWithName(templateName);
    } else {
      // For mobile, use Alert.prompt
      Alert.prompt(
        'Save as Template',
        'Enter a name for this template:',
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Save',
            onPress: async (name) => {
              if (name) {
                await saveTemplateWithName(name);
              }
            }
          }
        ],
        'plain-text',
        `${businessOwner.companyName || 'My'} Product Template`
      );
    }
  };

  const saveTemplateWithName = async (templateName: string) => {
    try {
      // Convert products to template format (remove id, businessId, createdAt, updatedAt)
      const templateProducts = products.map(product => {
        const { id, businessId, createdAt, updatedAt, ...templateProduct } = product;
        return templateProduct;
      });

      // Determine category from products
      const categories = [...new Set(products.map(p => p.category))];
      const categoryName = categories.length === 1 ? categories[0] : 'Mixed Categories';
      const categoryId = categories.length === 1 ? categories[0].toLowerCase().replace(/\s+/g, '_') : 'mixed';
      const icon = products[0]?.icon || '📦';

      // Save template
      const savedTemplate = await templateService.saveCustomProductTemplate({
        name: templateName,
        categoryName,
        icon,
        products: templateProducts,
        businessOwnerId: businessOwner?.id,
      });

      Alert.alert('Success', `Template "${templateName}" saved successfully! You can reuse this template later.`);
    } catch (error) {
      console.error('Error saving template:', error);
      Alert.alert('Error', 'Failed to save template. Please try again.');
    }
  };

  const handleExportTemplate = async () => {
    if (!businessOwner || products.length === 0) {
      Alert.alert('Error', 'Please add products before exporting as a template.');
      return;
    }

    try {
      // Convert products to template format
      const templateProducts = products.map(product => {
        const { id, businessId, createdAt, updatedAt, ...templateProduct } = product;
        return templateProduct;
      });

      const categories = [...new Set(products.map(p => p.category))];
      const categoryName = categories.length === 1 ? categories[0] : 'Mixed Categories';
      const icon = products[0]?.icon || '📦';

      const template = {
        name: `${businessOwner.companyName || 'My'} Product Template`,
        categoryName,
        icon,
        products: templateProducts,
      };

      // Export as JSON
      const jsonContent = await templateService.exportProductTemplateAsJSON(template);

      if (Platform.OS === 'web') {
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `product_template_${Date.now()}.json`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        Alert.alert('Success', 'Template exported as JSON!');
      } else {
        Alert.alert('Info', 'JSON export is available on web. Please use the web version to export templates.');
      }
    } catch (error) {
      console.error('Error exporting template:', error);
      Alert.alert('Error', 'Failed to export template. Please try again.');
    }
  };

  const handleDownloadTemplate = async () => {
    if (!businessOwner) {
      Alert.alert('Error', 'Please log in as a business owner to download templates.');
      return;
    }
    
    // Detect business type from industry
    const industryLower = businessOwner.industry?.toLowerCase() || '';
    let templateType = 'butcher'; // default
    
    if (industryLower.includes('halal')) {
      templateType = 'halal_meat';
    } else if (industryLower.includes('butcher') || industryLower.includes('meat')) {
      templateType = 'butcher';
    } else if (industryLower.includes('bakery') || industryLower.includes('bread')) {
      templateType = 'bakery';
    } else if (industryLower.includes('coffee') || industryLower.includes('cafe')) {
      templateType = 'coffee_shop';
    }
    
    try {
      const template = getTemplateByBusinessType(templateType);
      if (!template) {
        Alert.alert('Error', 'Template not found');
        return;
      }
      
      // Convert template to CSV
      const csvHeader = 'Product Name,Product Image,Description,Price,Unit,Category,Subcategory,Active,Inventory (In Stock),Stock Quantity,Weight,Preparation Time,Specifications,Tags,Option 1 Name,Option 1 Values,Option 1 Required,Option 2 Name,Option 2 Values,Option 2 Required,Option 3 Name,Option 3 Values,Option 3 Required\n';
      const csvRows = template.products.map(product => {
        const specs = typeof product.specifications === 'object' 
          ? JSON.stringify(product.specifications).replace(/"/g, '""')
          : (product.specifications || '');
        const tags = Array.isArray(product.tags) ? product.tags.join(';') : (product.tags || '');
        const imageUrl = product.images && product.images.length > 0 ? product.images[0] : '';
        const isActive = product.isActive !== undefined ? (product.isActive ? 'Yes' : 'No') : 'Yes';
        const isInStock = product.isInStock !== undefined ? (product.isInStock ? 'Yes' : 'No') : 'Yes';
        
        // Format product options (up to 3 options)
        const options = product.options || [];
        const option1Name = options[0]?.name || '';
        const option1Values = options[0]?.values?.map(v => v.name).join(';') || '';
        const option1Required = options[0]?.isRequired ? 'Yes' : 'No';
        
        const option2Name = options[1]?.name || '';
        const option2Values = options[1]?.values?.map(v => v.name).join(';') || '';
        const option2Required = options[1]?.isRequired ? 'Yes' : 'No';
        
        const option3Name = options[2]?.name || '';
        const option3Values = options[2]?.values?.map(v => v.name).join(';') || '';
        const option3Required = options[2]?.isRequired ? 'Yes' : 'No';
        
        return `"${product.name}","${imageUrl}","${product.description || ''}","${product.price}","${product.unit}","${product.category}","${product.subcategory || ''}","${isActive}","${isInStock}","${product.stockQuantity || ''}","${product.weight || ''}","${product.preparationTime || ''}","${specs}","${tags}","${option1Name}","${option1Values}","${option1Required}","${option2Name}","${option2Values}","${option2Required}","${option3Name}","${option3Values}","${option3Required}"`;
      });
      const csvContent = csvHeader + csvRows.join('\n');
      
      // Create blob and download (web) or share (mobile)
      if (Platform.OS === 'web') {
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `product_template_${templateType}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        Alert.alert('Success', 'Template CSV downloaded! Fill in your products and import it.');
      } else {
        // For mobile, we'd need to use a file sharing library
        Alert.alert('Info', 'CSV download is available on web. Please use the web version to download templates.');
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      Alert.alert('Error', 'Failed to download template');
    }
  };

  /**
   * Export the live catalog as CSV, including a "Product ID" column so the
   * same file can be edited and re-imported to update existing products
   * in place (see handleCSVFileSelect) instead of creating duplicates.
   */
  const handleExportProductsCSV = () => {
    if (!businessOwner || products.length === 0) {
      Alert.alert('Error', 'You have no products to export yet.');
      return;
    }
    if (Platform.OS !== 'web') {
      Alert.alert('Info', 'CSV export is available on web. Please use the web version to export your products.');
      return;
    }

    try {
      const rows = products.map(product => {
        const options = product.options || [];
        const opt = (i: number) => options[i];
        const optValues = (i: number) => opt(i)?.values?.map(v => v.name).join(';') || '';

        return [
          product.id,
          product.name,
          product.description || '',
          product.price,
          product.unit,
          product.category,
          product.subcategory || '',
          product.isActive ? 'Yes' : 'No',
          product.isInStock ? 'Yes' : 'No',
          product.stockQuantity ?? '',
          product.weight ?? '',
          product.preparationTime ?? '',
          (product.specifications || []).join(';'),
          (product.tags || []).join(';'),
          (product.images || []).join(';'),
          opt(0)?.name || '', optValues(0), opt(0)?.isRequired ? 'Yes' : 'No',
          opt(1)?.name || '', optValues(1), opt(1)?.isRequired ? 'Yes' : 'No',
          opt(2)?.name || '', optValues(2), opt(2)?.isRequired ? 'Yes' : 'No',
        ].map(csvEscapeField).join(',');
      });

      const csvContent = [PRODUCT_CSV_HEADER.map(csvEscapeField).join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `products_${businessOwner.id}_${Date.now()}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      Alert.alert('Success', 'Products exported! Edit the file and re-import it to update your catalog — keep the "Product ID" column as-is to update existing products instead of creating duplicates.');
    } catch (error) {
      console.error('Error exporting products CSV:', error);
      Alert.alert('Error', 'Failed to export products.');
    }
  };

  const importTemplate = async (templateType: string) => {
    console.log('📦 importTemplate called with type:', templateType);
    try {
      const template = getTemplateByBusinessType(templateType);
      console.log('📦 Template found:', template ? template.categoryName : 'NOT FOUND');
      
      if (!template || !businessOwner) {
        console.error('❌ Template or business owner missing');
        Alert.alert('Error', 'Template not found');
        return;
      }
      
      // Create products from template
      const newProducts = createProductsFromTemplate(template, businessOwner.id);
      console.log('📦 Created products:', newProducts.length);
      
      // Count only active products that will be added (all template products are active by default)
      const newActiveProducts = newProducts.filter(p => p.isActive).length;
      const currentActiveProducts = products.filter(p => p.isActive).length;
      const totalAfter = currentActiveProducts + newActiveProducts;
      
      console.log('📦 Template Import Validation:', {
        currentActive: currentActiveProducts,
        newActive: newActiveProducts,
        totalAfter: totalAfter,
        subscriptionStatus: subscriptionStatus,
        productLimit: subscriptionStatus?.productLimit,
        limit: subscriptionStatus?.productLimit || 10
      });
      
      // Check product limit before importing
      const limitCheck = checkProductLimit(newActiveProducts);
      
      console.log('📦 Limit Check Result:', {
        canAdd: limitCheck.canAdd,
        message: limitCheck.message
      });
      
      if (!limitCheck.canAdd) {
        // Update subscription status with current local product count before showing prompt
        const status = await productSubscriptionService.getStatus(businessOwner.id);
        console.log('📦 Backend Status:', status);
        setSubscriptionStatus({
          ...status,
          activeProducts: currentActiveProducts, // Use local count for accuracy
        });
        setShowUpgradePrompt(true);
        
        // Create a clearer error message that mentions the template size
        const limit = subscriptionStatus?.productLimit || 10;
        const remaining = Math.max(0, limit - currentActiveProducts);
        const errorMessage = `The "${template.categoryName}" template contains ${newProducts.length} products, but you can only add ${remaining} more product${remaining !== 1 ? 's' : ''} with your current plan (${limit} product limit).\n\nYou currently have ${currentActiveProducts} active product${currentActiveProducts !== 1 ? 's' : ''}. If you import this template, you would have ${totalAfter} products total, which exceeds your limit of ${limit}.\n\n${remaining > 0 ? `You can import up to ${remaining} products from this template, or upgrade your plan to import all ${newProducts.length} products.` : 'Please upgrade your plan to import this template.'}`;
        
        Alert.alert('Product Limit Reached', errorMessage);
        return;
      }
      
      // Show preview
      Alert.alert(
        `Import ${template.categoryName} Products?`,
        `This will add ${newProducts.length} products to your catalog:\n\n${newProducts.slice(0, 5).map(p => `• ${p.name} - $${p.price}/${p.unit}`).join('\n')}${newProducts.length > 5 ? `\n... and ${newProducts.length - 5} more` : ''}\n\nYou can edit or remove any products after import.`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: `Import ${newProducts.length} Products`,
            onPress: async () => {
              console.log('✅ Importing products...');
              const updatedProducts = [...products, ...newProducts];
              setProducts(updatedProducts);
              
              // Close template modal immediately for better UX
              setShowTemplateModal(false);
              
              // Save products
              await saveProducts(updatedProducts, businessOwner.id);
              
              // Increment product count in background (don't wait for it)
              productSubscriptionService.incrementCount(businessOwner.id, newProducts.length).catch(err => {
                // Silently handle errors - this is non-critical
                if (__DEV__) {
                  console.warn('Background product count increment failed:', err);
                }
              });
              
              console.log('✅ Products saved successfully');
              Alert.alert('✅ Success!', `Imported ${newProducts.length} products successfully!\n\nYou can now edit prices, stock levels, and details.`);
            }
          }
        ]
      );
    } catch (error) {
      console.error('❌ Error importing template:', error);
      Alert.alert('Error', `Failed to import template: ${error.message || 'Unknown error'}`);
    }
  };

  // Helper function to format specifications safely
  // Supports multiple formats:
  // - Array of strings (RECOMMENDED for multiple cuts): ['Short Ribs', 'Ribeye', 'Sirloin']
  // - Object with cuts array: { cuts: ['Short Ribs', 'Ribeye'] }
  // - Object with single cut: { cut: 'Short Ribs' }
  // - Array of objects: [{ cut: 'Short Ribs' }, { cut: 'Ribeye' }]
  // - Legacy format (for backward compatibility): { cut: 'Short Ribs', origin: 'Local' } - origin is removed
  const formatSpecifications = (specs: any): string => {
    if (!specs) return '';
    
    // If it's already a string, return it
    if (typeof specs === 'string') return specs;
    
    // If it's an array
    if (Array.isArray(specs)) {
      // Check if it's an array of objects
      if (specs.length > 0 && typeof specs[0] === 'object' && specs[0] !== null) {
        // Array of objects: [{ cut: 'Short Ribs' }, { cut: 'Ribeye' }]
        return specs.map((spec: any) => {
          if (spec.cut) {
            return spec.cut; // Just return the cut name, no origin
          }
          // Filter out origin from other fields
          return Object.entries(spec)
            .filter(([key]) => key !== 'origin')
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
        }).join(', ');
      }
      // Array of strings: ['Short Ribs', 'Ribeye', 'Sirloin'] - BEST FORMAT FOR MULTIPLE CUTS
      return specs.join(', ');
    }
    
    // If it's an object
    if (typeof specs === 'object' && specs !== null) {
      // Object with cuts array: { cuts: ['Short Ribs', 'Ribeye'] } - BEST FORMAT FOR MULTIPLE CUTS
      if (specs.cuts && Array.isArray(specs.cuts)) {
        return specs.cuts.join(', ');
      }
      // Object with single cut: { cut: 'Short Ribs' }
      if (specs.cut) {
        return specs.cut; // Just return the cut name, no origin
      }
      // Other object format - remove origin field
      const filteredEntries = Object.entries(specs)
        .filter(([key]) => key !== 'origin') // Remove origin field
        .filter(([key, value]) => value !== null && value !== undefined && value !== '') // Remove empty values
        .map(([key, value]) => `${key}: ${value}`);
      
      // Return empty string if no valid entries
      if (filteredEntries.length === 0) {
        return '';
      }
      
      return filteredEntries.join(', ');
    }
    
    return '';
  };

  const handleCSVImport = () => {
    setShowCSVImportModal(true);
  };

  const handleCSVFileUpload = async (file: File | null) => {
    if (!file) return;
    
    try {
      setCsvFileName(file.name);
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        // Auto-import if content is valid
        if (text.trim()) {
          handleCSVFileSelect(text);
        } else {
          Alert.alert('Error', 'CSV file is empty');
          setCsvFileName('');
        }
      };
      reader.onerror = () => {
        Alert.alert('Error', 'Failed to read CSV file');
        setCsvFileName('');
      };
      reader.readAsText(file);
    } catch (error) {
      console.error('Error reading file:', error);
      Alert.alert('Error', 'Failed to read CSV file');
      setCsvFileName('');
    }
  };

  /**
   * Import products from CSV. Rows whose "Product ID" matches an existing
   * product (e.g. from a prior handleExportProductsCSV download) update that
   * product in place; unmatched rows are added as new products. This makes
   * export -> edit -> re-import a safe round trip instead of creating
   * duplicates on every re-upload.
   */
  const handleCSVFileSelect = async (csvContent: string) => {
    try {
      const rows = parseCSV(csvContent);
      if (rows.length < 2) {
        Alert.alert('Error', 'CSV file is empty or invalid');
        return;
      }

      const header = rows[0].map(h => h.trim());
      const findCol = (needle: string) => header.findIndex(h => h.toLowerCase().includes(needle));

      const idIndex = findCol('product id');
      const nameIndex = findCol('name'); // matches "Product Name"
      const priceIndex = findCol('price');
      const unitIndex = findCol('unit');
      const categoryIndex = findCol('category');
      const descriptionIndex = findCol('description');
      const subcategoryIndex = findCol('subcategory');
      const activeIndex = findCol('active');
      const inventoryIndex = findCol('inventory');
      const stockIndex = findCol('stock quantity');
      const weightIndex = findCol('weight');
      const prepIndex = findCol('preparation');
      const specsIndex = findCol('specification');
      const tagsIndex = findCol('tag');
      const imagesIndex = findCol('product images') !== -1 ? findCol('product images') : findCol('product image');

      if (nameIndex === -1 || priceIndex === -1 || unitIndex === -1 || categoryIndex === -1) {
        Alert.alert('Error', 'CSV must contain: Product Name, Price, Unit, Category');
        return;
      }

      const existingById = new Map(products.map(p => [p.id, p]));
      const parsedRows: Array<{ product: Product; isUpdate: boolean }> = [];

      for (let i = 1; i < rows.length; i++) {
        const values = rows[i];
        if (!values[nameIndex] || !values[priceIndex]) continue;

        const rowId = idIndex !== -1 ? (values[idIndex] || '').trim() : '';
        const existing = rowId ? existingById.get(rowId) : undefined;

        const options: ProductOption[] = [];
        for (let o = 0; o < 3; o++) {
          const optNameIdx = findCol(`option ${o + 1} name`);
          const optValuesIdx = findCol(`option ${o + 1} values`);
          const optRequiredIdx = findCol(`option ${o + 1} required`);
          if (optNameIdx !== -1 && values[optNameIdx]) {
            const optValues = (values[optValuesIdx] || '').split(';').filter(Boolean).map((val, idx) => ({
              id: `opt${o + 1}_val_${idx}`,
              name: val.trim(),
              priceModifier: undefined,
            }));
            if (optValues.length > 0) {
              options.push({
                id: `opt${o + 1}_${Date.now()}_${i}`,
                name: values[optNameIdx],
                values: optValues,
                isRequired: values[optRequiredIdx]?.toLowerCase() === 'yes',
              });
            }
          }
        }

        const specifications: string[] = (() => {
          if (specsIndex === -1) return existing?.specifications || [];
          const raw = (values[specsIndex] || '').trim();
          if (!raw) return [];
          if (raw.startsWith('[')) {
            try {
              const parsed = JSON.parse(raw);
              if (Array.isArray(parsed)) return parsed.map(String);
            } catch {
              // fall through to semicolon-split below
            }
          }
          return raw.split(';').map(s => s.trim()).filter(Boolean);
        })();

        // Images pasted/edited in the CSV are treated as already-hosted URLs
        // (not re-uploaded). Unchanged URLs keep their existing generated
        // thumbnail; new/edited URLs fall back to the full image at render
        // time (same fallback used when pasting an image URL in the form).
        const importedImages = imagesIndex !== -1
          ? (values[imagesIndex] || '').split(';').map(s => s.trim()).filter(Boolean)
          : undefined;
        const importedThumbnails = importedImages?.map(url => {
          const existingIdx = existing?.images?.indexOf(url) ?? -1;
          return existingIdx !== -1 ? (existing?.imageThumbnails?.[existingIdx] || '') : '';
        });

        const product: Product = {
          ...(existing || {}),
          id: existing?.id || `csv_${Date.now()}_${i}`,
          businessId: businessOwner?.id || existing?.businessId || '',
          name: values[nameIndex],
          description: descriptionIndex !== -1 ? (values[descriptionIndex] || '') : (existing?.description || ''),
          price: parseFloat(values[priceIndex]) || 0,
          unit: (values[unitIndex] || existing?.unit || 'piece') as Product['unit'],
          category: values[categoryIndex] || existing?.category || '',
          subcategory: subcategoryIndex !== -1 ? (values[subcategoryIndex] || '') : (existing?.subcategory || ''),
          isActive: activeIndex !== -1 ? values[activeIndex]?.toLowerCase() !== 'no' : (existing?.isActive ?? true),
          isInStock: inventoryIndex !== -1 ? values[inventoryIndex]?.toLowerCase() !== 'no' : (existing?.isInStock ?? true),
          stockQuantity: stockIndex !== -1 && values[stockIndex] ? (parseInt(values[stockIndex], 10) || 0) : existing?.stockQuantity,
          weight: weightIndex !== -1 && values[weightIndex] ? (parseFloat(values[weightIndex]) || undefined) : existing?.weight,
          specifications,
          preparationTime: prepIndex !== -1 && values[prepIndex] ? (parseInt(values[prepIndex], 10) || 0) : (existing?.preparationTime || 0),
          icon: existing?.icon || '🛒',
          images: importedImages !== undefined ? (importedImages.length > 0 ? importedImages : undefined) : existing?.images,
          imageThumbnails: importedImages !== undefined ? importedThumbnails : existing?.imageThumbnails,
          tags: tagsIndex !== -1 ? (values[tagsIndex]?.split(';').filter(Boolean) || []) : (existing?.tags || []),
          options: options.length > 0 ? options : existing?.options,
          createdAt: existing?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        parsedRows.push({ product, isUpdate: !!existing });
      }

      if (parsedRows.length === 0) {
        Alert.alert('Error', 'No valid products found in CSV');
        return;
      }

      const newRows = parsedRows.filter(r => !r.isUpdate);
      const updateRows = parsedRows.filter(r => r.isUpdate);

      // Check product limit before importing (only net-new rows count)
      if (businessOwner) {
        const newActiveProducts = newRows.filter(r => r.product.isActive !== false).length;
        const limitCheck = checkProductLimit(newActiveProducts);

        if (!limitCheck.canAdd) {
          const status = await productSubscriptionService.getStatus(businessOwner.id);
          const localActiveProducts = products.filter(p => p.isActive).length;
          setSubscriptionStatus({
            ...status,
            activeProducts: localActiveProducts,
          });
          setShowUpgradePrompt(true);
          setShowCSVImportModal(false);
          setCsvFileName('');
          Alert.alert('Product Limit Reached', limitCheck.message);
          return;
        }
      }

      const updateMap = new Map(updateRows.map(r => [r.product.id, r.product]));
      const updatedProducts = [
        ...products.map(p => updateMap.get(p.id) || p),
        ...newRows.map(r => r.product),
      ];
      setProducts(updatedProducts);

      // Close modal immediately for better UX
      setShowCSVImportModal(false);
      setCsvFileName('');

      if (businessOwner) {
        await saveProducts(updatedProducts, businessOwner.id);

        if (newRows.length > 0) {
          // Increment product count in background (don't wait for it)
          productSubscriptionService.incrementCount(businessOwner.id, newRows.length).catch(err => {
            // Silently handle errors - this is non-critical
            if (__DEV__) {
              console.warn('Background product count increment failed:', err);
            }
          });
        }
      }

      const summary = [
        newRows.length > 0 ? `${newRows.length} added` : '',
        updateRows.length > 0 ? `${updateRows.length} updated` : '',
      ].filter(Boolean).join(', ');
      Alert.alert('Success', `CSV processed: ${summary}.`);
    } catch (error) {
      console.error('Error importing CSV:', error);
      Alert.alert('Error', 'Failed to import CSV. Please check the format.');
    }
  };

  const handleToggleFeaturedOnWidget = async (product: Product) => {
    try {
      const owner = businessOwner;
      if (!owner) return;

      const next = !product.featuredOnWidget;
      if (next) {
        const usage = await getWidgetFeaturedUsage(owner.id);
        const already = !!product.featuredOnWidget;
        if (!already && usage.used >= MAX_WIDGET_FEATURED_SLOTS) {
          Alert.alert(
            'Website widget limit',
            `You can feature up to ${MAX_WIDGET_FEATURED_SLOTS} services and products combined on your embedded widget. Turn off another featured item in Service or Product management.`
          );
          return;
        }
      }

      const updatedProducts = products.map(p =>
        p.id === product.id ? { ...p, featuredOnWidget: next } : p
      );
      setProducts(updatedProducts);
      await saveProducts(updatedProducts, owner.id);
    } catch (error) {
      console.error('Error toggling widget feature:', error);
      Alert.alert('Error', 'Failed to update widget visibility');
    }
  };

  const renderProductCard = (product: Product) => (
    <View key={product.id} style={[
      styles.productCard,
      !product.isActive && styles.inactiveProductCard,
      !product.isInStock && styles.outOfStockCard
    ]}>
      <View style={styles.productHeader}>
        <View style={styles.productInfo}>
          <Text style={styles.productIcon}>{product.icon}</Text>
          <View style={styles.productDetails}>
            <View style={styles.productNameRow}>
              <Text
                style={[
                  styles.productName,
                  !product.isActive && styles.inactiveText,
                  !product.isInStock && styles.outOfStockText
                ]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {product.name}
              </Text>
              {!product.isActive && <Text style={styles.statusBadge}>DISCONTINUED</Text>}
              {!product.isInStock && product.isActive && <Text style={styles.stockBadge}>OUT OF STOCK</Text>}
              {product.options && product.options.length > 0 && (
                <Text style={styles.optionsBadge}>
                  {product.options.length} Option{product.options.length > 1 ? 's' : ''}
                </Text>
              )}
            </View>
            {(product.category || product.subcategory) && (
              <Text style={styles.productCategory}>
                {product.category || ''}{product.category && product.subcategory ? ' • ' : ''}{product.subcategory || ''}
              </Text>
            )}
            <Text style={styles.productPrice}>${product.price}/{product.unit}</Text>
            {product.options && product.options.length > 0 && (
              <View style={styles.optionsPreview}>
                <Text style={styles.optionsPreviewText}>
                  Options: {product.options.map(opt => opt.name).filter(Boolean).join(', ') || 'Configured'}
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
      <View style={styles.productActionsRow}>
        {Platform.OS === 'web' ? (
          <>
            <View style={styles.toggleContainer}>
              <Text style={styles.toggleLabel}>Active</Text>
              <View style={styles.switchTouchTarget}>
                <Switch
                  value={product.isActive}
                  onValueChange={() => handleToggleActive(product)}
                  trackColor={{ false: '#767577', true: '#34C759' }}
                  thumbColor={product.isActive ? '#FFFFFF' : '#f4f3f4'}
                />
              </View>
            </View>
            {product.isActive && (
              <View style={styles.toggleContainer}>
                <Text style={styles.toggleLabel}>In Stock</Text>
                <View style={styles.switchTouchTarget}>
                  <Switch
                    value={product.isInStock}
                    onValueChange={() => handleToggleStock(product)}
                    trackColor={{ false: '#767577', true: '#FF9500' }}
                    thumbColor={product.isInStock ? '#FFFFFF' : '#f4f3f4'}
                  />
                </View>
              </View>
            )}
          </>
        ) : (
          <>
            <TouchableOpacity
              style={styles.toggleContainer}
              onPress={() => handleToggleActive(product)}
              activeOpacity={0.7}
              collapsable={false}
            >
              <Text style={styles.toggleLabel}>Active</Text>
              <View style={styles.switchTouchTarget} pointerEvents="none">
                <Switch
                  value={product.isActive}
                  trackColor={{ false: '#767577', true: '#34C759' }}
                  thumbColor={product.isActive ? '#FFFFFF' : '#f4f3f4'}
                />
              </View>
            </TouchableOpacity>
            {product.isActive && (
              <TouchableOpacity
                style={styles.toggleContainer}
                onPress={() => handleToggleStock(product)}
                activeOpacity={0.7}
                collapsable={false}
              >
                <Text style={styles.toggleLabel}>In Stock</Text>
                <View style={styles.switchTouchTarget} pointerEvents="none">
                  <Switch
                    value={product.isInStock}
                    trackColor={{ false: '#767577', true: '#FF9500' }}
                    thumbColor={product.isInStock ? '#FFFFFF' : '#f4f3f4'}
                  />
                </View>
              </TouchableOpacity>
            )}
          </>
        )}
      </View>

      <View style={styles.widgetFeatureRow}>
        <Text style={styles.widgetFeatureLabel}>Show on website widget</Text>
        <Switch
          value={!!product.featuredOnWidget}
          onValueChange={() => handleToggleFeaturedOnWidget(product)}
          disabled={!product.isActive}
          trackColor={{ false: '#767577', true: '#667eea' }}
          thumbColor={product.featuredOnWidget ? '#FFFFFF' : '#f4f3f4'}
        />
      </View>
      
      <Text style={styles.productDescription}>{product.description}</Text>
      
      {(() => {
        const formattedSpecs = formatSpecifications(product.specifications);
        if (!formattedSpecs || formattedSpecs.trim() === '' || formattedSpecs === '.') {
          return null;
        }
        return (
        <View style={styles.specificationsContainer}>
          <Text style={styles.specificationsTitle}>Specifications:</Text>
            <Text style={styles.specifications}>{formattedSpecs}</Text>
        </View>
        );
      })()}

      <View style={styles.productFooter}>
        <View style={styles.productStats}>
          {product.stockQuantity !== undefined && (
            <View style={styles.stockContainer}>
              <Text style={[
                styles.stockText,
                product.stockQuantity <= (product.lowStockThreshold || 5) && styles.lowStockText,
                product.stockQuantity === 0 && styles.outOfStockText
              ]}>
                Stock: {product.stockQuantity}
                {product.lowStockThreshold && product.stockQuantity <= product.lowStockThreshold ? ' ⚠️' : ''}
              </Text>
            </View>
          )}
          {product.weight && product.weight > 0 ? (
            <Text style={styles.weightText}>Weight: {product.weight} {product.unit || 'lb'}</Text>
          ) : null}
        </View>
        
        <View style={styles.productButtons}>
          <TouchableOpacity
            style={styles.editButton}
            onPress={() => handleEditProduct(product)}
          >
            <Text style={styles.editButtonText}>Edit</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={() => handleDeleteProduct(product)}
          >
            <Text style={styles.deleteButtonText}>Delete</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#007AFF" />
        <Text style={styles.loadingText}>Loading products...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>← Back</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Product Management</Text>
        <TouchableOpacity style={styles.addButton} onPress={handleAddProduct}>
          <Text style={styles.addButtonText}>+ Add</Text>
        </TouchableOpacity>
      </View>
      <View style={styles.widgetHintBanner}>
        <Text style={styles.widgetHintText}>
          Website widget: feature up to {MAX_WIDGET_FEATURED_SLOTS} services and products combined (toggle below or in Service management).
        </Text>
      </View>

      {/* Quick Action Buttons */}
      <View style={styles.quickActions}>
        <TouchableOpacity style={styles.quickActionButton} onPress={handleAddProduct}>
          <Text style={styles.quickActionIcon}>➕</Text>
          <Text style={styles.quickActionText}>Add Product</Text>
        </TouchableOpacity>
        {products.length > 0 && (
          <>
            <TouchableOpacity style={styles.quickActionButton} onPress={handleSaveAsTemplate}>
              <Text style={styles.quickActionIcon}>💾</Text>
              <Text style={styles.quickActionText}>Save Template</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.quickActionButton} onPress={handleExportTemplate}>
              <Text style={styles.quickActionIcon}>📤</Text>
              <Text style={styles.quickActionText}>Export JSON</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.quickActionButton} onPress={handleExportProductsCSV}>
              <Text style={styles.quickActionIcon}>📄</Text>
              <Text style={styles.quickActionText}>Export CSV</Text>
            </TouchableOpacity>
          </>
        )}
        <TouchableOpacity style={styles.quickActionButton} onPress={handleDownloadTemplate}>
          <Text style={styles.quickActionIcon}>📥</Text>
          <Text style={styles.quickActionText}>Download Template</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.quickActionButton} onPress={handleCSVImport}>
          <Text style={styles.quickActionIcon}>📤</Text>
          <Text style={styles.quickActionText}>Import CSV</Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        style={styles.content}
        keyboardShouldPersistTaps="handled"
        scrollEventThrottle={16}
      >
        {products.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyIcon}>🛒</Text>
            <Text style={styles.emptyTitle}>No Products Yet</Text>
            <Text style={styles.emptySubtitle}>
              Start with a template or add products manually
            </Text>
            <TouchableOpacity style={styles.addFirstButton} onPress={handleDownloadTemplate}>
              <Text style={styles.addFirstButtonText}>📥 Download Template</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[styles.addFirstButton, styles.addManualButton]} onPress={handleAddProduct}>
              <Text style={styles.addFirstButtonText}>➕ Add Manually</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            <View style={styles.statsContainer}>
              <Text style={styles.statsText}>
                {products.length} Products • {products.filter(p => p.isActive).length} Active
              </Text>
            </View>
            {products.map(renderProductCard)}
          </>
        )}
      </ScrollView>

      {/* Add/Edit Product Modal */}
      <Modal
        visible={showAddModal}
        animationType="slide"
        presentationStyle="pageSheet"
      >
        <SafeAreaView style={styles.modalContainer}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </Text>
            <TouchableOpacity onPress={() => setShowAddModal(false)}>
              <Text style={styles.modalCloseButton}>✕</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.modalContent}>
            <View style={styles.formGroup}>
              <Text style={styles.label}>Product Name *</Text>
              <AppTextInput
                style={styles.input}
                value={formData.name}
                onChangeText={(text) => setFormData({...formData, name: text})}
                placeholder="Enter product name"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Description</Text>
              <AppTextInput
                style={[styles.input, styles.textArea]}
                value={formData.description}
                onChangeText={(text) => setFormData({...formData, description: text})}
                placeholder="Enter product description"
                multiline
                numberOfLines={3}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Product images ({formData.images.length} / {imagesPerProduct})</Text>
              <Text style={styles.helperText}>
                Upload images (stored in Firebase) or add a URL. Your plan allows up to {imagesPerProduct} images per product.
              </Text>
              <View style={styles.imageSpecBox}>
                <Text style={styles.imageSpecTitle}>Photo specs</Text>
                <Text style={styles.imageSpecLine}>
                  Size: {RECOMMENDED_IMAGE_WIDTH}×{RECOMMENDED_IMAGE_HEIGHT} px (square, 1:1)
                </Text>
                <Text style={styles.imageSpecLine}>
                  Target file size: under {RECOMMENDED_FILE_SIZE_KB} KB for fast uploads
                </Text>
                <Text style={styles.imageSpecLine}>
                  Max file size: {MAX_FILE_SIZE_BYTES / (1024 * 1024)} MB · Formats: {ACCEPTED_FORMATS_LABEL}
                </Text>
                <Text style={styles.imageSpecHint}>
                  Photos are automatically resized and compressed on upload — no need to resize beforehand.
                </Text>
              </View>
              {formData.images.length > 0 && (
                <View style={styles.imageThumbnails}>
                  {formData.images.map((uri, index) => (
                    <View key={`${uri.slice(-12)}-${index}`} style={styles.imageThumbWrap}>
                      <Image source={{ uri: formData.imageThumbnails[index] || uri }} style={styles.imageThumb} resizeMode="cover" />
                      <TouchableOpacity
                        style={styles.imageThumbRemove}
                        onPress={() => removeImage(index)}
                        accessibilityLabel="Remove image"
                      >
                        <Text style={styles.imageThumbRemoveText}>✕</Text>
                      </TouchableOpacity>
                    </View>
                  ))}
                </View>
              )}
              {formData.images.length < imagesPerProduct && (
                <>
                  {Platform.OS === 'web' && (
                    <>
                      <input
                        type="file"
                        ref={imageFileInputRef}
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        style={{ display: 'none' }}
                        onChange={handleImageFileSelect}
                      />
                      <TouchableOpacity
                        style={[styles.uploadImageButton, uploadingImage && styles.uploadImageButtonDisabled]}
                        onPress={() => imageFileInputRef.current?.click()}
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? (
                          <ActivityIndicator size="small" color="#fff" />
                        ) : (
                          <Text style={styles.uploadImageButtonText}>Upload image</Text>
                        )}
                      </TouchableOpacity>
                    </>
                  )}
                  <View style={styles.imageUrlRow}>
                    <AppTextInput
                      style={[styles.input, styles.imageUrlInput]}
                      value={imageUrlInput}
                      onChangeText={setImageUrlInput}
                      placeholder="Or paste image URL"
                      autoCapitalize="none"
                      keyboardType="url"
                    />
                    <TouchableOpacity style={styles.addUrlButton} onPress={addImageUrl}>
                      <Text style={styles.addUrlButtonText}>Add URL</Text>
                    </TouchableOpacity>
                  </View>
                </>
              )}
            </View>

            <View style={styles.row}>
              <View style={styles.halfWidth}>
                <Text style={styles.label}>Price *</Text>
                <AppTextInput
                  style={styles.input}
                  value={formData.price}
                  onChangeText={(text) => setFormData({...formData, price: text})}
                  placeholder="Enter price"
                  keyboardType="decimal-pad"
                />
              </View>
              <View style={styles.halfWidth}>
                <Text style={styles.label}>Unit *</Text>
                <ScrollView horizontal style={styles.unitSelector}>
                  {units.map(unit => (
                    <TouchableOpacity
                      key={unit}
                      style={[
                        styles.unitButton,
                        formData.unit === unit && styles.unitButtonActive
                      ]}
                      onPress={() => setFormData({...formData, unit})}
                    >
                      <Text style={[
                        styles.unitButtonText,
                        formData.unit === unit && styles.unitButtonTextActive
                      ]}>
                        {unit}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Category *</Text>
              <AppTextInput
                style={styles.input}
                value={formData.category}
                onChangeText={(text) => setFormData({...formData, category: text})}
                placeholder="Enter category"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Subcategory</Text>
              <AppTextInput
                style={styles.input}
                value={formData.subcategory}
                onChangeText={(text) => setFormData({...formData, subcategory: text})}
                placeholder="Enter subcategory"
              />
            </View>

            <View style={styles.row}>
              <View style={styles.halfWidth}>
                <Text style={styles.label}>Weight</Text>
                <AppTextInput
                  style={styles.input}
                  value={formData.weight}
                  onChangeText={(text) => setFormData({...formData, weight: text})}
                  placeholder="Enter weight"
                  keyboardType="decimal-pad"
                />
              </View>
              <View style={styles.halfWidth}>
                <Text style={styles.label}>Stock Quantity</Text>
                <AppTextInput
                  style={styles.input}
                  value={formData.stockQuantity}
                  onChangeText={(text) => setFormData({...formData, stockQuantity: text})}
                  placeholder="Enter stock quantity"
                  keyboardType="number-pad"
                />
              </View>
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Preparation Time (minutes)</Text>
              <AppTextInput
                style={styles.input}
                value={formData.preparationTime}
                onChangeText={(text) => setFormData({...formData, preparationTime: text})}
                placeholder="Enter preparation time"
                keyboardType="number-pad"
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Specifications</Text>
              <AppTextInput
                style={[styles.input, styles.textArea]}
                value={formData.specifications}
                onChangeText={(text) => setFormData({...formData, specifications: text})}
                placeholder="Enter specifications"
                multiline
                numberOfLines={2}
              />
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Tags (comma separated)</Text>
              <AppTextInput
                style={styles.input}
                value={formData.tags}
                onChangeText={(text) => setFormData({...formData, tags: text})}
                placeholder="Enter tags"
              />
            </View>

            {/* Product Options Section */}
            <View style={styles.formGroup}>
              <View style={styles.optionsHeader}>
                <Text style={styles.label}>Product Options (Optional)</Text>
                {productOptions.length < 3 && (
                  <TouchableOpacity
                    style={styles.addOptionButton}
                    onPress={handleAddOption}
                  >
                    <Text style={styles.addOptionButtonText}>+ Add Option</Text>
                  </TouchableOpacity>
                )}
              </View>
              <Text style={styles.helperText}>
                Add up to 3 customizable options (e.g., Size, Packaging Size, Color)
              </Text>

              {productOptions.map((option, optionIndex) => (
                <View key={option.id} style={styles.optionCard}>
                  <View style={styles.optionHeader}>
                    <AppTextInput
                      style={[styles.input, styles.optionNameInput]}
                      value={option.name}
                      onChangeText={(text) => handleUpdateOptionName(optionIndex, text)}
                      placeholder="Option name (e.g., Size, Packaging Size)"
                    />
                    <TouchableOpacity
                      style={styles.removeOptionButton}
                      onPress={() => handleRemoveOption(optionIndex)}
                    >
                      <Text style={styles.removeOptionButtonText}>✕</Text>
                    </TouchableOpacity>
                  </View>

                  <View style={styles.optionControls}>
                    <View style={styles.requiredToggle}>
                      <Text style={styles.requiredLabel}>Required</Text>
                      <Switch
                        value={option.isRequired}
                        onValueChange={() => handleToggleOptionRequired(optionIndex)}
                      />
                    </View>
                  </View>

                  <View style={styles.valuesSection}>
                    <Text style={styles.valuesLabel}>Values:</Text>
                    {option.values.map((value, valueIndex) => (
                      <View key={value.id} style={styles.valueRow}>
                        <AppTextInput
                          style={[styles.input, styles.valueNameInput]}
                          value={value.name}
                          onChangeText={(text) => handleUpdateOptionValue(optionIndex, valueIndex, 'name', text)}
                          placeholder="Value name (e.g., Small, All in one)"
                        />
                        <AppTextInput
                          style={[styles.input, styles.priceModifierInput]}
                          value={value.priceModifier !== undefined ? value.priceModifier.toString() : ''}
                          onChangeText={(text) => {
                            const numValue = text === '' ? undefined : parseFloat(text);
                            handleUpdateOptionValue(optionIndex, valueIndex, 'priceModifier', numValue || 0);
                          }}
                          placeholder="Price +/-"
                          keyboardType="decimal-pad"
                        />
                        <TouchableOpacity
                          style={styles.removeValueButton}
                          onPress={() => handleRemoveOptionValue(optionIndex, valueIndex)}
                        >
                          <Text style={styles.removeValueButtonText}>✕</Text>
                        </TouchableOpacity>
                      </View>
                    ))}
                    <TouchableOpacity
                      style={styles.addValueButton}
                      onPress={() => handleAddOptionValue(optionIndex)}
                    >
                      <Text style={styles.addValueButtonText}>+ Add Value</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ))}
            </View>

            <View style={styles.formGroup}>
              <Text style={styles.label}>Icon</Text>
              <ScrollView horizontal style={styles.iconSelector}>
                {productIcons.map(icon => (
                  <TouchableOpacity
                    key={icon}
                    style={[
                      styles.iconButton,
                      formData.icon === icon && styles.iconButtonActive
                    ]}
                    onPress={() => setFormData({...formData, icon})}
                  >
                    <Text style={styles.iconButtonText}>{icon}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          </ScrollView>

          <View style={styles.modalFooter}>
            <TouchableOpacity
              style={styles.cancelModalButton}
              onPress={() => setShowAddModal(false)}
            >
              <Text style={styles.cancelModalButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.saveModalButton}
              onPress={handleSaveProduct}
            >
              <Text style={styles.saveModalButtonText}>
                {editingProduct ? 'Update Product' : 'Add Product'}
              </Text>
            </TouchableOpacity>
          </View>
        </SafeAreaView>
      </Modal>

      {/* CSV Import Modal */}
      <Modal
        visible={showCSVImportModal}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setShowCSVImportModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.csvModalContent}>
            <View style={styles.csvModalHeader}>
              <Text style={styles.csvModalTitle}>Import Products from CSV</Text>
              <TouchableOpacity onPress={() => setShowCSVImportModal(false)}>
                <Text style={styles.csvModalClose}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.csvModalBody}>
              <Text style={styles.csvInstructions}>
                Upload a CSV file to import products. Required columns: Product Name, Price, Unit, Category.{'\n\n'}
                Editing your catalog in bulk? Use "Export CSV" to download your current products, edit the file, and re-upload it here — rows with a "Product ID" that matches an existing product will update it in place instead of creating a duplicate. Rows without a matching ID are added as new products.
              </Text>
              
              {/* File Upload Button */}
              {Platform.OS === 'web' ? (
                <View style={styles.csvUploadSection}>
                  {/* Hidden file input for web */}
                  {Platform.OS === 'web' && (
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      style={{ display: 'none' }}
                      ref={(input) => {
                        if (input) {
                          fileInputRef.current = input;
                        }
                      }}
                      onChange={(e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) {
                          handleCSVFileUpload(file);
                        }
                      }}
              />
                  )}
              <TouchableOpacity
                    style={styles.csvUploadButton}
                onPress={() => {
                      if (Platform.OS === 'web' && fileInputRef.current) {
                        fileInputRef.current.click();
                  }
                }}
              >
                    <Text style={styles.csvUploadButtonText}>📁 Upload CSV File</Text>
              </TouchableOpacity>
                  {csvFileName ? (
                    <Text style={styles.csvFileName}>Selected: {csvFileName}</Text>
                  ) : (
                    <Text style={styles.csvFileName}>No file selected</Text>
                  )}
                </View>
              ) : (
                <View style={styles.csvUploadSection}>
                  <Text style={styles.csvMobileNote}>
                    CSV file upload is available on web browsers. Please use the web version to upload your CSV file.
                  </Text>
                </View>
              )}
              
              <Text style={styles.csvNote}>
                Select a CSV file with your product data. The file will be automatically processed and products will be added to your catalog.
              </Text>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Upgrade Prompt Modal */}
      {subscriptionStatus && (
        <UpgradePrompt
          visible={showUpgradePrompt}
          currentPlan={subscriptionStatus.planName || 'Free'}
          limit={subscriptionStatus.productLimit || 10}
          activeProducts={products.filter(p => p.isActive).length}
          onUpgrade={() => {
            setShowUpgradePrompt(false);
            if (onNavigateToSubscription) {
              onNavigateToSubscription();
            } else {
            Alert.alert(
              'Upgrade',
              'Navigate to subscription management to upgrade your plan.',
              [
                { text: 'Cancel', style: 'cancel' },
                { text: 'OK', onPress: () => {
                  console.log('Navigate to upgrade');
                }}
              ]
            );
            }
          }}
          onDismiss={() => setShowUpgradePrompt(false)}
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  backButton: {
    padding: 8,
  },
  backButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  addButton: {
    padding: 8,
  },
  addButtonText: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  widgetHintBanner: {
    backgroundColor: '#eef2ff',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  widgetHintText: {
    fontSize: 12,
    color: '#444',
    lineHeight: 16,
  },
  widgetFeatureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
    paddingVertical: 4,
  },
  widgetFeatureLabel: {
    fontSize: 14,
    color: '#444',
    fontWeight: '500',
    flex: 1,
    paddingRight: 8,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  emptyContainer: {
    alignItems: 'center',
    marginTop: 60,
    paddingHorizontal: 40,
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 20,
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  emptySubtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginBottom: 30,
  },
  addFirstButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  addFirstButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  statsContainer: {
    backgroundColor: 'white',
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  statsText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  productCard: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    overflow: 'hidden',
  },
  inactiveProductCard: {
    backgroundColor: '#f8f8f8',
    opacity: 0.7,
    borderLeftWidth: 4,
    borderLeftColor: '#FF3B30',
  },
  outOfStockCard: {
    backgroundColor: '#fff5f5',
    borderLeftWidth: 4,
    borderLeftColor: '#FF9500',
  },
  productHeader: {
    marginBottom: 8,
  },
  productInfo: {
    flexDirection: 'row',
    flex: 1,
    minWidth: 0,
  },
  productIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  productDetails: {
    flex: 1,
    minWidth: 0,
  },
  productNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  productName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginRight: 8,
  },
  inactiveText: {
    color: '#999',
    textDecorationLine: 'line-through',
  },
  outOfStockText: {
    color: '#FF9500',
  },
  statusBadge: {
    backgroundColor: '#FF3B30',
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 8,
  },
  stockBadge: {
    backgroundColor: '#FF9500',
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 8,
  },
  optionsBadge: {
    backgroundColor: '#007AFF',
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    marginLeft: 6,
  },
  optionsPreview: {
    marginTop: 4,
  },
  optionsPreviewText: {
    fontSize: 11,
    color: '#007AFF',
    fontStyle: 'italic',
  },
  productCategory: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
  },
  productPrice: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
  productActionsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
    marginBottom: 12,
  },
  toggleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  switchTouchTarget: {
    minWidth: 44,
    minHeight: 26,
    justifyContent: 'center',
    zIndex: 1,
    transform: [{ scale: 0.82 }],
  },
  toggleLabel: {
    fontSize: 12,
    color: '#666',
    fontWeight: '500',
  },
  productDescription: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
    lineHeight: 20,
  },
  specificationsContainer: {
    marginBottom: 12,
  },
  specificationsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  specifications: {
    fontSize: 12,
    color: '#666',
  },
  productFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  productStats: {
    flex: 1,
  },
  stockContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  stockText: {
    fontSize: 12,
    color: '#666',
    marginRight: 8,
  },
  lowStockText: {
    color: '#FF9500',
    fontWeight: '600',
  },
  quickStockButton: {
    padding: 4,
  },
  quickStockButtonText: {
    fontSize: 16,
  },
  weightText: {
    fontSize: 12,
    color: '#666',
    marginBottom: 2,
  },
  prepTimeText: {
    fontSize: 12,
    color: '#666',
  },
  productButtons: {
    flexDirection: 'row',
  },
  editButton: {
    backgroundColor: '#2563EB', // Professional blue
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
  },
  editButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  deleteButton: {
    backgroundColor: '#EF4444', // Red for delete actions only
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  deleteButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'white',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  cancelButton: {
    fontSize: 16,
    color: '#FF3B30',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  saveButton: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  modalCloseButton: {
    fontSize: 24,
    color: '#666',
    fontWeight: '300',
  },
  modalFooter: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E1E5E9',
    gap: 12,
  },
  cancelModalButton: {
    flex: 1,
    backgroundColor: '#F8F9FA',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelModalButtonText: {
    color: '#1C1C1E',
    fontSize: 16,
    fontWeight: '600',
  },
  saveModalButton: {
    flex: 1,
    backgroundColor: '#2563EB', // Professional blue
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  saveModalButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#f9f9f9',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  halfWidth: {
    width: '48%',
  },
  unitSelector: {
    flexDirection: 'row',
  },
  unitButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    marginRight: 8,
  },
  unitButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  unitButtonText: {
    fontSize: 14,
    color: '#666',
  },
  unitButtonTextActive: {
    color: 'white',
  },
  iconSelector: {
    flexDirection: 'row',
  },
  iconButton: {
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    marginRight: 8,
  },
  iconButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  iconButtonText: {
    fontSize: 20,
  },
  quickActions: {
    flexDirection: 'row',
    padding: 16,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    gap: 12,
  },
  quickActionButton: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#007AFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  quickActionIcon: {
    fontSize: 24,
    marginBottom: 4,
  },
  quickActionText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#007AFF',
    textAlign: 'center',
  },
  addManualButton: {
    backgroundColor: '#007AFF',
    marginTop: 12,
  },
  // Product Options Styles
  optionsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  addOptionButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  addOptionButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  helperText: {
    fontSize: 12,
    color: '#666',
    marginBottom: 12,
    fontStyle: 'italic',
  },
  imageSpecBox: {
    backgroundColor: '#f0f4ff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d0daf5',
    padding: 12,
    marginBottom: 12,
  },
  imageSpecTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  imageSpecLine: {
    fontSize: 12,
    color: '#444',
    lineHeight: 18,
  },
  imageSpecHint: {
    fontSize: 11,
    color: '#666',
    marginTop: 6,
    fontStyle: 'italic',
  },
  imageThumbnails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  imageThumbWrap: {
    position: 'relative',
    width: 72,
    height: 72,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#eee',
  },
  imageThumb: {
    width: '100%',
    height: '100%',
  },
  imageThumbRemove: {
    position: 'absolute',
    top: 4,
    right: 4,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  imageThumbRemoveText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  uploadImageButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 8,
  },
  uploadImageButtonDisabled: {
    opacity: 0.6,
  },
  uploadImageButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  imageUrlRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  imageUrlInput: {
    flex: 1,
    marginBottom: 0,
  },
  addUrlButton: {
    backgroundColor: '#34C759',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  addUrlButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  optionCard: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  optionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  optionNameInput: {
    flex: 1,
    marginRight: 8,
  },
  removeOptionButton: {
    backgroundColor: '#EF4444', // Red for delete actions only
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeOptionButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  optionControls: {
    marginBottom: 12,
  },
  requiredToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  requiredLabel: {
    fontSize: 14,
    color: '#333',
  },
  valuesSection: {
    marginTop: 8,
  },
  valuesLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  valueNameInput: {
    flex: 1,
    marginRight: 8,
  },
  priceModifierInput: {
    width: 80,
    marginRight: 8,
  },
  removeValueButton: {
    backgroundColor: '#EF4444', // Red for delete actions only
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  removeValueButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  addValueButton: {
    backgroundColor: '#2563EB', // Professional blue
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  addValueButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  csvModalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    width: '90%',
    maxHeight: '80%',
  },
  csvModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  csvModalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1C1C1E',
  },
  csvModalClose: {
    fontSize: 24,
    color: '#666',
  },
  csvModalBody: {
    padding: 20,
  },
  csvInstructions: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
    lineHeight: 20,
  },
  csvUploadSection: {
    marginBottom: 16,
    alignItems: 'center',
  },
  csvUploadButton: {
    backgroundColor: '#34C759',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 8,
    minWidth: 200,
  },
  csvUploadButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  csvFileName: {
    fontSize: 12,
    color: '#007AFF',
    marginTop: 4,
    fontStyle: 'italic',
  },
  csvMobileNote: {
    fontSize: 12,
    color: '#666',
    textAlign: 'center',
    fontStyle: 'italic',
    padding: 8,
  },
  csvDivider: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    marginVertical: 12,
    fontWeight: '600',
  },
  csvInput: {
    minHeight: 200,
    fontFamily: Platform.OS === 'web' ? 'monospace' : 'monospace',
    fontSize: 12,
  },
  csvNote: {
    fontSize: 12,
    color: '#999',
    marginTop: 12,
    fontStyle: 'italic',
  },
  csvImportButton: {
    backgroundColor: '#007AFF',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  csvImportButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ProductManagementScreen;
